import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Mail, GraduationCap, BookOpen, Award, ArrowLeft, Loader, ExternalLink } from 'lucide-react';
import { fetchDosenByIdentifier, getImageUrl } from '../utils/api';

const PRODI_MAP = {
  kesmas: 'Kesehatan Masyarakat',
  keperawatan: 'Keperawatan',
  farmasi: 'Farmasi',
  ners: 'Profesi Ners',
};

const PRODI_COLOR_MAP = {
  kesmas:      'from-orange-400 to-amber-500',    // Kuning
  farmasi:     'from-blue-600 to-blue-800',        // Biru
  keperawatan: 'from-emerald-600 to-teal-700',    // Hijau
  ners:        'from-rose-500 to-red-700',         // Merah
};

const getInitials = (name) =>
  name?.replace(/^(Dr\.|apt\.|Ns\.|Drs\.)?\s*/, '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '??';

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
    <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
      <Icon size={15} className="text-purple-500" />
    </div>
    <div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800 dark:text-white">{value}</p>
    </div>
  </div>
);

const DosenDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dosen, setDosen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchDosenByIdentifier(slug);
        if (data) {
          setDosen(data);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        setNotFound(true);
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-[70px]">
      <Loader className="animate-spin text-purple-500" size={40} />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-[70px] text-gray-500">
      <p className="text-lg font-medium mb-4">Data dosen tidak ditemukan</p>
      <Link to="/dosen-tendik" className="text-purple-500 hover:text-purple-700 flex items-center gap-2">
        <ArrowLeft size={16} /> Kembali ke Dosen & Tendik
      </Link>
    </div>
  );

  const colorClass = PRODI_COLOR_MAP[dosen.prodi] || 'from-[#7B2FBE] to-[#9333EA]';
  const prodi = PRODI_MAP[dosen.prodi] || dosen.prodi || '-';

  return (
    <div className="min-h-screen bg-[#F8F7FF] dark:bg-[#121020] pt-[70px]">
      {/* Hero */}
      <div className="page-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-6">
            <Link to="/" className="hover:text-white flex items-center gap-1">
              <Home size={14} /> Beranda
            </Link>
            <ChevronRight size={14} />
            <Link to="/dosen-tendik" className="hover:text-white">Dosen & Tendik</Link>
            <ChevronRight size={14} />
            <span className="text-white line-clamp-1">{dosen.nama}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-purple-500 hover:text-purple-700 mb-8 transition-colors"
        >
          <ArrowLeft size={16} /> Kembali
        </button>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">

          {/* Kolom kiri — foto & identitas */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="md:col-span-2"
          >
            <div className="glass-card rounded-2xl overflow-hidden">
              {/* Foto */}
              <div className={`py-6 bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                <div className="relative p-2 bg-white/20 backdrop-blur-md rounded-2xl shadow-xl">
                  {dosen.foto ? (
                    <img
                      src={getImageUrl(dosen.foto)}
                      alt={dosen.nama}
                      className="w-36 h-40 rounded-xl object-cover border-2 border-white/80 shadow-inner"
                    />
                  ) : (
                    <div className="w-36 h-40 rounded-xl bg-white/20 flex items-center justify-center text-white text-5xl font-bold border-2 border-white/80 shadow-inner">
                      {getInitials(dosen.nama)}
                    </div>
                  )}
                </div>
              </div>

              {/* Info dasar */}
              <div className="p-5 text-center">
                <span className="text-xs px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium">
                  Dosen
                </span>
                <h1 className="text-lg font-bold text-gray-800 dark:text-white mt-3 leading-tight">
                  {dosen.nama}
                </h1>
                <p className="text-purple-600 dark:text-purple-400 text-sm font-medium mt-1">
                  {dosen.jabatan_fungsional || '-'}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {prodi}
                </p>
                {dosen.email && (
                  <a
                    href={`mailto:${dosen.email}`}
                    title={dosen.email}
                    className="flex items-center justify-center gap-1.5 text-sm text-purple-500 hover:text-purple-700 mt-3 transition-colors max-w-full px-4"
                  >
                    <Mail size={14} className="flex-shrink-0" />
                    <span className="truncate">{dosen.email}</span>
                  </a>
                )}

                {dosen.link_sinta && (
                  <a
                    href={dosen.link_sinta}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl
                               bg-gradient-to-r from-[#7B2FBE] to-[#9333EA]
                               text-white text-sm font-semibold shadow-lg shadow-purple-500/30
                               hover:shadow-purple-500/50 hover:scale-105
                               transition-all duration-300 ease-out"
                  >
                    {/* SINTA custom icon */}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 flex-shrink-0"
                      aria-hidden="true"
                    >
                      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z" />
                      <path d="M8 12a4 4 0 0 1 8 0" />
                      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                    </svg>
                    Profil SINTA
                    <ExternalLink
                      size={12}
                      className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200"
                    />
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          {/* Kolom kanan — detail */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="md:col-span-3 space-y-6"
          >
            {/* Identitas Akademik */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <GraduationCap size={18} className="text-purple-500" />
                Identitas Akademik
              </h2>
              <div className="space-y-0">
                {dosen.nidn && <InfoRow icon={Award} label="NIDN" value={dosen.nidn} />}
                {dosen.nuptk && <InfoRow icon={Award} label="NUPTK" value={dosen.nuptk} />}
                {dosen.nik && <InfoRow icon={Award} label="NIK" value={dosen.nik} />}
                <InfoRow icon={GraduationCap} label="Pendidikan Terakhir" value={dosen.pendidikan_terakhir || '-'} />
                <InfoRow icon={BookOpen} label="Bidang Keahlian" value={dosen.bidang_keahlian || '-'} />
                <InfoRow icon={Award} label="Program Studi" value={prodi} />
              </div>
            </div>

            {/* Jabatan */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <Award size={18} className="text-purple-500" />
                Jabatan
              </h2>
              <div className="space-y-0">
                <InfoRow icon={Award} label="Jabatan Fungsional" value={dosen.jabatan_fungsional || '-'} />
                <InfoRow icon={Award} label="Jabatan Struktural" value={dosen.jabatan_struktural?.jabatan || '-'} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DosenDetail;
